/**
 * Type definitions for PlatinumGraphs component
 *
 * This module contains all TypeScript interfaces for the charting system,
 * with improved naming (row/column instead of category/month).
 */

import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import type { ChartDesign, ChartSortOrder } from '../../types/gotypes'

/**
 * Available chart types that can be rendered
 */
export type ChartType =
  | 'table'         // Simple HTML table view
  | 'pie'
  | 'bar'           // Horizontal bar chart (snapshot)
  | 'column'        // Vertical column chart (snapshot)
  | 'line'
  | 'stackedBar'    // Horizontal stacked bar
  | 'clusteredBar'  // Horizontal clustered (grouped) bar
  | 'stackedColumn' // Vertical stacked column
  | 'clusteredColumn'// Vertical clustered (grouped) column
  | 'scatter';

/**
 * Data type selector for chart values
 */
export type DataType = 'freq' | 'colPct' | 'rowPct';

/**
 * Configuration for a graph instance
 * This can be saved/loaded to persist user preferences
 */
export interface GraphConfig {
  /** Type of chart to render */
  chartType: ChartType;

  /** Which data type to display (frequency, column %, or row %) */
  dataType: DataType;

  /** Selected rows to display (renamed from categories) */
  selectedRows: string[];

  /** Selected columns to display (renamed from months) */
  selectedColumns: string[];

  /** Single column selection for bar/column charts (renamed from selectedMonth) */
  selectedColumn?: string;

  /** Switch X/Y axes by transposing the data */
  switchAxis: boolean;

  /**
   * Print the value above each column as a data label (#1201). Clustered
   * column only today — Sky asked to read the emotion split off the chart
   * rather than off a tooltip.
   *
   * OPT-IN, DEFAULT OFF: the generators are shared by every dashboard and
   * exec report in the product, and turning labels on globally would change
   * every chart in it. Percentages print at 0dp, matching the axis ticks
   * beside them; frequencies print as counts.
   */
  showDataLabels?: boolean;

  /**
   * Pie only: leave out the top-centre caption naming the one column shown.
   * Off by default; for a pie whose column name says nothing to the reader.
   */
  hideColumnCaption?: boolean;

  /**
   * Bar order for the bar-family charts (#1808): a dashboard widget's
   * `sort_order`. Absent keeps each chart type's pre-#1808 order.
   */
  sortOrder?: ChartSortOrder;

  /** Explanation of why this visualization is helpful (for AI-generated graphs) */
  explanation?: string;
}

/**
 * Parsed table format used internally by chart generators
 * This is the intermediate format between PlatinumData and ECharts options
 */
export interface ParsedTable {
  /** Column headers (e.g., month names, category labels) */
  headers: string[];

  /**
   * Raw cell values as strings.
   * Each inner array represents a data row.
   * The first element of each inner array is often the series/row name.
   */
  rows: string[][];

  /** Number of data rows */
  rowCnt: number;

  /** Number of columns, matching the length of `headers` */
  colCnt: number;
}

/**
 * Settings passed to chart generator functions
 */
export interface ChartGeneratorSettings {
  /** Data type for formatting */
  dataType?: DataType;

  /** Selected column header for bar/column charts */
  selectedHeader?: string;

  /** Selected rows to filter */
  selectedRows?: string[];

  /** Selected columns to filter */
  selectedColumns?: string[];

  /** Color map for series colors (key = series name, value = hex color) */
  colorMap?: Record<string, string>;

  /** Gradient map for series colors (key = series name, value = ECharts gradient object) */
  gradientMap?: Record<string, object>;

  /** Print the value above each column as a data label (#1201) — see GraphConfig.showDataLabels. */
  showDataLabels?: boolean;

  /** Pie only: leave out the top-centre caption naming the column — see GraphConfig.hideColumnCaption. */
  hideColumnCaption?: boolean;

  /**
   * Bar order on the bar-family charts (#1808), in reading order. Absent
   * keeps each generator's pre-#1808 order. See utils/chartSort.ts.
   */
  sortOrder?: ChartSortOrder;

  /**
   * Rows to plot, by POSITION in the table handed to the generator (#1808).
   * Wins over `selectedRows`, which matches by name and so cannot tell two
   * rows with the same label apart. Empty or absent falls back to names.
   */
  selectedRowPositions?: number[];

  /** Allow any other settings */
  [key: string]: any;
}

/**
 * Props for the main PlatinumGraphs component
 */
export interface PlatinumGraphsProps {
  /** PlatinumData from the backend */
  data: PlatinumData;

  /** Current graph configuration */
  config: GraphConfig;

  /** Callback when configuration changes */
  onConfigChange?: (config: GraphConfig) => void;

  /** Whether to show controls (default: true) */
  showControls?: boolean;

  /** Optional CSS class name */
  className?: string;

  /** Optional loading state */
  isLoading?: boolean;

  /** Optional error message */
  error?: string | null;

  /** Row types for Base type filtering */
  allRowTypes?: string[];

  /** Column types for Base type filtering */
  allColumnTypes?: string[];

  /** When true, removes minHeight constraint and respects parent container height */
  respectParentHeight?: boolean;

  /**
   * Called with a series name when the viewer clicks it in the legend (#968
   * Part A: direct manipulation on the visual instead of a separate config
   * tool). Omit to leave the legend inert.
   *
   * One-way by design: the chart engine filters unselected series out of the
   * data entirely (see chartFilters.filterTableData), so a removed series has
   * no legend item left to click again. Callers give the viewer somewhere else
   * to put it back.
   */
  onLegendToggle?: (seriesName: string) => void;

  /** Color map for series colors (key = series name lowercase, value = hex color) */
  colorMap?: Record<string, string>;

  /** Gradient map for series colors (key = series name lowercase, value = ECharts gradient object) */
  gradientMap?: Record<string, object>;

  /** When true, hides the chart legend */
  hideLegend?: boolean;

  /**
   * Chart design overrides (#968 Part C) — axis label styling, axis titles,
   * gridlines. Resolved against design-system defaults via resolveChartDesign
   * (utils/chartDesign.ts) and merged onto the generated option via
   * applyChartDesign (utils/applyChartDesign.ts). Undefined means every
   * property inherits the default.
   */
  design?: ChartDesign;
}

/**
 * Props for the GraphControls component
 */
export interface GraphControlsProps {
  /** All available rows from data */
  allRows: string[];

  /** All available columns from data */
  allColumns: string[];

  /** Currently selected rows */
  selectedRows: string[];

  /** Currently selected columns */
  selectedColumns: string[];

  /** Currently selected single column (for bar/column) */
  selectedColumn?: string;

  /** Current chart type */
  chartType: ChartType;

  /** Current data type */
  dataType: DataType;

  /** Validated allowed chart types based on data */
  allowedChartTypes: ChartType[];

  /** Callback when chart type changes */
  onChartTypeChange: (chartType: ChartType) => void;

  /** Callback when data type changes */
  onDataTypeChange: (dataType: DataType) => void;

  /** Callback when rows selection changes */
  onRowsChange: (rows: string[]) => void;

  /** Callback when columns selection changes */
  onColumnsChange: (columns: string[]) => void;

  /** Callback when single column changes */
  onSingleColumnChange: (column: string) => void;

  /** Switch axis flag */
  switchAxis: boolean;

  /** Callback when switch axis changes */
  onSwitchAxisChange: (checked: boolean) => void;

  /** Callback when close button is clicked */
  onClose?: () => void;
}

/**
 * Lookup table mapping table dimensions to permissible chart kinds
 * Key format: `${rowBucket}-${colBucket}` where bucket = '1' or '2+'
 */
export const CHART_GUIDE: Record<string, ChartType[]> = {
  // 1 Row, 1 Column
  '1-1': ['table', 'pie', 'bar', 'column', 'line'],

  // 1 Row, 2+ Columns
  '1-2+': ['table', 'pie', 'bar', 'column', 'line', 'scatter'],

  // 2+ Rows, 1 Column
  '2+-1': ['table', 'pie', 'bar', 'column', 'line', 'scatter'],

  // 2+ Rows, 2+ Columns
  '2+-2+': [
    'table',
    'line',
    'bar',
    'column',
    'stackedBar',
    'clusteredBar',
    'stackedColumn',
    'clusteredColumn',
    'scatter',
  ],
}

/**
 * Available data type options for selectors
 */
export const DATA_TYPE_OPTIONS: Array<{ value: DataType; label: string }> = [
  { value: 'freq', label: 'Frequencies' },
  { value: 'colPct', label: 'Column %' },
  { value: 'rowPct', label: 'Row %' },
]

/**
 * Display names for chart types
 */
export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  table: 'Table',
  pie: 'Pie',
  bar: 'Bar',
  column: 'Column',
  line: 'Line',
  stackedBar: 'Stacked Bar',
  clusteredBar: 'Clustered Bar',
  stackedColumn: 'Stacked Column',
  clusteredColumn: 'Clustered Column',
  scatter: 'Scatter',
}

/**
 * Options for capturing chart screenshots
 */
export interface CaptureOptions {
  /** Image format (default: 'png') */
  type?: 'png' | 'jpeg';

  /** Pixel ratio for higher resolution (default: 2) */
  pixelRatio?: number;

  /** Background color (default: '#fff') */
  backgroundColor?: string;

  /** Exclude components (like toolbox, dataZoom) from screenshot */
  excludeComponents?: string[];
}

/**
 * Props for PlatinumGraphScreenshot component
 */
export interface PlatinumGraphScreenshotProps {
  /** PlatinumData from the backend */
  data: PlatinumData;

  /** Current graph configuration */
  config: GraphConfig;

  /** Operation mode: 'capture' to render and capture, 'preview' to display existing image */
  mode: 'capture' | 'preview';

  /** Callback when screenshot is ready (for 'capture' mode) */
  onScreenshotReady?: (base64: string) => void;

  /** Base64 image string to display (for 'preview' mode) */
  previewImage?: string;

  /** If true, display the image after capturing (for 'capture' mode) */
  displayAfterCapture?: boolean;

  /** Screenshot capture options */
  captureOptions?: CaptureOptions;

  /** Callback when an error occurs */
  onError?: (error: string) => void;

  /** Optional CSS class name */
  className?: string;

  /** Optional loading state */
  isLoading?: boolean;
}
